import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from '../locales/en/common.json';
import deCommon from '../locales/de/common.json';
import enSettings from '../locales/en/settings.json';
import deSettings from '../locales/de/settings.json';
import enBilling from '../locales/en/billing.json';
import deBilling from '../locales/de/billing.json';
import enChat from '../locales/en/chat.json';
import deChat from '../locales/de/chat.json';
import enGeneration from '../locales/en/generation.json';
import deGeneration from '../locales/de/generation.json';
import enApps from '../locales/en/apps.json';
import deApps from '../locales/de/apps.json';
import enImageStudio from '../locales/en/imageStudio.json';
import deImageStudio from '../locales/de/imageStudio.json';
import enImageStudioSettings from '../locales/en/imageStudioSettings.json';
import deImageStudioSettings from '../locales/de/imageStudioSettings.json';
import enImageStudioToolbar from '../locales/en/imageStudioToolbar.json';
import deImageStudioToolbar from '../locales/de/imageStudioToolbar.json';
import enInfotexts from '../locales/en/infotexts.json';
import deInfotexts from '../locales/de/infotexts.json';
import enToolsApps from '../locales/en/toolsApps.json';
import deToolsApps from '../locales/de/toolsApps.json';
import enPlatformPages from '../locales/en/platformPages.json';
import dePlatformPages from '../locales/de/platformPages.json';
import enCancelPage from '../locales/en/cancelPage.json';
import deCancelPage from '../locales/de/cancelPage.json';
import enChangelog from '../locales/en/changelog.json';
import deChangelog from '../locales/de/changelog.json';
import enTours from '../locales/en/tours.json';
import deTours from '../locales/de/tours.json';

// Zentrale i18n-Infrastruktur (Task #76, Phase 1). Englisch ist Fallback-/Default-Sprache
// (Nutzer-Entscheidung 2026-07-22). Neue Sprachen: hier zu SUPPORTED_LANGUAGES + resources ergaenzen.
// Neue Bereiche/Strings: eigenen Namespace unter src/locales/{de,en}/<ns>.json anlegen und in
// resources + ns registrieren (inkrementell pro App-Bereich, siehe TASKS.md #76 Phase 2).
export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const resources = {
    en: { common: enCommon, settings: enSettings, billing: enBilling, chat: enChat, generation: enGeneration, apps: enApps, imageStudio: enImageStudio, imageStudioSettings: enImageStudioSettings, imageStudioToolbar: enImageStudioToolbar, infotexts: enInfotexts, toolsApps: enToolsApps, platformPages: enPlatformPages, cancelPage: enCancelPage, changelog: enChangelog, tours: enTours },
    de: { common: deCommon, settings: deSettings, billing: deBilling, chat: deChat, generation: deGeneration, apps: deApps, imageStudio: deImageStudio, imageStudioSettings: deImageStudioSettings, imageStudioToolbar: deImageStudioToolbar, infotexts: deInfotexts, toolsApps: deToolsApps, platformPages: dePlatformPages, cancelPage: deCancelPage, changelog: deChangelog, tours: deTours },
} as const;

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'en',
        supportedLngs: [...SUPPORTED_LANGUAGES],
        // Nur der Sprachcode (de statt de-DE) - wir pflegen keine Regionalvarianten.
        load: 'languageOnly',
        ns: ['common', 'settings', 'billing', 'chat', 'generation', 'apps', 'imageStudio', 'imageStudioSettings', 'imageStudioToolbar', 'infotexts', 'toolsApps', 'platformPages', 'cancelPage', 'changelog', 'tours'],
        defaultNS: 'common',
        interpolation: { escapeValue: false }, // React escaped bereits selbst
        detection: {
            // Zuerst die gespeicherte Wahl, sonst die Browsersprache. Persistenz in localStorage.
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: 'sengi_lang',
            caches: ['localStorage'],
        },
        // Ressourcen sind gebuendelt (synchron), es gibt keinen Suspense-Boundary in main.tsx.
        react: { useSuspense: false },
    });

// <html lang> mit der aktiven Sprache synchron halten - wichtig fuer Screenreader/A11y (Task #78)
// und korrekte Browser-Silbentrennung/Rechtschreibpruefung.
const applyHtmlLang = (lng: string) => {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = (lng || 'en').split('-')[0];
    }
};
applyHtmlLang(i18n.language);
i18n.on('languageChanged', applyHtmlLang);

export default i18n;
